import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import {
  DateCell,
  ListCell,
  MoneyCell,
  MutedCell,
  NameCell,
  NumberCell,
  StatusChip,
} from './TableCells'
import { countTargetingTypes } from '@/services/targeting/targeting'
import type { AxProduct } from '@/services/earnings/axProductsApi'

export interface ProductTableProps {
  products: AxProduct[]
  loading?: boolean
  emptyMessage?: string
  selection?: AxProduct[]
  onSelectionChange?: (products: AxProduct[]) => void
}

/** The marketplace products of a network, as a sortable, selectable list. */
export function ProductTable({
  products,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
}: ProductTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<AxProduct, unknown>[] = [
    ...(selectable ? [selectionColumn<AxProduct>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 220 },
      cell: ({ row }) => <NameCell name={row.original.name} sub={row.original.description} />,
    },
    {
      accessorKey: 'type',
      header: t('common.type'),
      size: 130,
      cell: ({ row }) => <MutedCell value={t(`products.type_${row.original.type}`)} />,
    },
    {
      id: 'owner',
      header: t('products.owner'),
      enableSorting: false,
      size: 150,
      cell: ({ row }) => (
        <MutedCell value={row.original.ownerTeam?.name ?? t('products.ownerNetwork')} />
      ),
    },
    {
      accessorKey: 'minimumCpm',
      header: t('products.minimumCpm'),
      enableSorting: false,
      size: 130,
      cell: ({ row }) => <MoneyCell value={row.original.minimumCpm} />,
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<AxProduct>({
      id: 'cpmSpecification',
      header: t('products.cpmValue'),
      enableSorting: false,
      size: 130,
      cell: ({ row }) => (
        <MutedCell value={t(`products.cpmSpecification_${row.original.cpmSpecification}`)} />
      ),
    }),
    optionalColumn<AxProduct>({
      id: 'bidStrategyOption',
      header: t('products.bidStrategy'),
      enableSorting: false,
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <MutedCell
          value={t(`products.bidStrategyOption_${row.original.bidStrategyOption ?? 'STANDARD_ONLY'}`)}
        />
      ),
    }),
    optionalColumn<AxProduct>({
      id: 'minimumBudget',
      header: t('products.minimumBudget'),
      enableSorting: false,
      size: 140,
      cell: ({ row }) => <MoneyCell value={row.original.minimumBudget} />,
    }),
    optionalColumn<AxProduct>({
      id: 'maximumBudget',
      header: t('products.maximumBudget'),
      enableSorting: false,
      size: 140,
      cell: ({ row }) => <MoneyCell value={row.original.maximumBudget} />,
    }),
    optionalColumn<AxProduct>({
      id: 'targeting',
      header: t('targeting.title'),
      accessorFn: (row) => countTargetingTypes(row.targeting),
      size: 120,
      cell: ({ row }) => <NumberCell value={countTargetingTypes(row.original.targeting)} />,
    }),
    optionalColumn<AxProduct>({
      id: 'teams',
      header: t('common.teams'),
      enableSorting: false,
      meta: { minWidth: 160 },
      cell: ({ row }) => <ListCell values={row.original.teams.map((team) => team.name)} />,
    }),
    optionalColumn<AxProduct>({
      id: 'layouts',
      header: t('products.layouts'),
      enableSorting: false,
      meta: { minWidth: 160 },
      cell: ({ row }) => <ListCell values={row.original.layouts.map((l) => l.name)} />,
    }),
    optionalColumn<AxProduct>({
      id: 'labels',
      header: t('common.labels'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.labels} />,
    }),
    optionalColumn<AxProduct>({
      id: 'dealId',
      header: t('products.dealId'),
      enableSorting: false,
      size: 140,
      cell: ({ row }) => <MutedCell value={row.original.dealId} />,
    }),
    optionalColumn<AxProduct>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<AxProduct>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={products}
      loading={loading}
      emptyMessage={emptyMessage ?? t('products.noProductsFound')}
      columnsKey="products"
      onRowClick={(row) => navigate(`/admin/products/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
    />
  )
}
